import { motion } from 'framer-motion';
import { Users, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useServerStatus } from '@/hooks/useServerStatus';
import { ServerStatusSkeleton } from '@/components/ui/Skeletons';
import { cn } from '@/lib/utils';

export function ServerStatus({ compact = false }) {
  const { status, loading, error, refetch } = useServerStatus();

  if (loading) {
    return <ServerStatusSkeleton />;
  }

  // ONLINE if status.online === true, OFFLINE otherwise
  const isOnline = status?.online === true;
  const playerCount = status?.playerCount ?? 0;
  const maxPlayers = status?.maxPlayers ?? 100;

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'w-2.5 h-2.5 rounded-full',
            isOnline ? 'status-online' : 'status-offline'
          )}
        />
        <span className="text-sm font-medium text-muted-foreground">
          {isOnline ? `${playerCount} Online` : 'Offline'}
        </span>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-xl border border-border bg-card/50 backdrop-blur-sm"
    >
      <div className="p-4 md:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Status indicator */}
            <div className="relative">
              <div
                className={cn(
                  'w-4 h-4 rounded-full',
                  isOnline ? 'status-online' : 'status-offline'
                )}
              />
              {isOnline && (
                <div className="absolute inset-0 rounded-full bg-green-500/50 animate-ping" />
              )}
            </div>

            {/* Status text */}
            <div>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'font-semibold',
                    isOnline ? 'text-green-500' : 'text-red-500'
                  )}
                >
                  Server {isOnline ? 'Online' : 'Offline'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                <Users className="h-4 w-4" />
                <span>
                  {playerCount} / {maxPlayers} Players
                </span>
              </div>
            </div>
          </div>

          {/* Refresh button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={refetch}
            className="shrink-0"
            aria-label="Refresh status"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Debug error message (non-fatal) - shows exact error */}
        {error && (
          <div className="mt-3 p-2 rounded bg-yellow-500/10 border border-yellow-500/30">
            <p className="text-xs text-yellow-500 font-mono break-all">
              Debug: {error}
            </p>
          </div>
        )}
      </div>

      {/* Progress bar for player count */}
      <div className="h-1 bg-muted">
        <motion.div
          className={cn(
            'h-full',
            isOnline ? 'bg-green-500' : 'bg-red-500'
          )}
          initial={{ width: 0 }}
          animate={{ width: `${(playerCount / maxPlayers) * 100}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </div>
    </motion.div>
  );
}
